<template>
    <nav class="left-menu" aria-label="Main Navigation">
        <ul class="accordion" id="accordion-items" role="presentation">
            <li class="section"
                 v-for="(item, sectionIdx) in menuData.categories"
                 :key="'section-' + sectionIdx">
                <!-- Hidden input for state logic, but accessible via label -->
                <input type="radio" name="accordion-1" :id="'section-' + sectionIdx" aria-hidden="true" />
                <label :for="'section-' + sectionIdx"
                       role="button"
                       tabindex="0"
                       :aria-expanded="false"
                       :aria-controls="'content-' + sectionIdx"
                       @click="handleAccordionClick($event, 'section-' + sectionIdx)"
                       @keydown.enter.prevent="handleAccordionClick($event, 'section-' + sectionIdx)"
                       @keydown.space.prevent="handleAccordionClick($event, 'section-' + sectionIdx)">
                    <span>{{ item.category }}</span>
                </label>
                <div class="content-wrapper" :id="'content-' + sectionIdx" role="region" :aria-label="item.category + ' items'">
                    <ul class="content">
                        <li v-for="(asset, index) in item.assetclasses"
                            class="sub-section"
                            @click="toggle"
                            :key="'section-' + sectionIdx + '-content-' + index"
                            :id="'section-' + sectionIdx + '-content-' + index">
                            <router-link :to="{ name: 'chart', params: { pathname: asset.pathname, year: selectedYear } }">
                                <span>{{ asset.name }}</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </li>
            <li class="section no-submenu">
                <input type="radio" name="accordion-1" id="transaction-costs" aria-hidden="true" />
                <label for="transaction-costs"
                       role="button"
                       tabindex="0"
                       @click="handleAccordionClick($event, 'transaction-costs', navigateToTransactionCosts)"
                       @keydown.enter.prevent="handleAccordionClick($event, 'transaction-costs', navigateToTransactionCosts)"
                       @keydown.space.prevent="handleAccordionClick($event, 'transaction-costs', navigateToTransactionCosts)">
                    <span>Transaction costs</span>
                </label>
            </li>
            <li class="section no-submenu mobile-only">
                <input type="radio" name="accordion-1" id="how-to-use" aria-hidden="true" />
                <label for="how-to-use"
                       role="button"
                       tabindex="0"
                       @click="handleAccordionClick($event, 'how-to-use', navigateToHowToUse)"
                       @keydown.enter.prevent="handleAccordionClick($event, 'how-to-use', navigateToHowToUse)"
                       @keydown.space.prevent="handleAccordionClick($event, 'how-to-use', navigateToHowToUse)">
                    <span>How to use</span>
                </label>
            </li>
            <li class="section no-submenu mobile-only">
                <input type="radio" name="accordion-1" id="respondents" aria-hidden="true" />
                <label for="respondents"
                       role="button"
                       tabindex="0"
                       @click="handleAccordionClick($event, 'respondents', navigateToRespondents)"
                       @keydown.enter.prevent="handleAccordionClick($event, 'respondents', navigateToRespondents)"
                       @keydown.space.prevent="handleAccordionClick($event, 'respondents', navigateToRespondents)">
                    <span>Respondents</span>
                </label>
            </li>
            <li class="section no-submenu mobile-only">
                <input type="radio" name="accordion-1" id="contact" aria-hidden="true" />
                <label for="contact"
                       role="button"
                       tabindex="0"
                       @click="handleAccordionClick($event, 'contact', navigateToContact)"
                       @keydown.enter.prevent="handleAccordionClick($event, 'contact', navigateToContact)"
                       @keydown.space.prevent="handleAccordionClick($event, 'contact', navigateToContact)">
                    <span>Contact us</span>
                </label>
            </li>
        </ul>
    </nav>
</template>

<script setup lang="ts">
    import { ref, onMounted, watch, nextTick } from 'vue'
    import { useRouter } from 'vue-router'
    import axios from 'axios'
    import { useYear } from '../composables/useYear'

    interface AssetClass {
      id: string
      name: string
      pathname: string
    }

    interface ChartItem {
      id: number
      category: string
      name: string
      pathname: string
      exclude?: boolean
    }

    interface MenuCategory {
      category: string
      assetclasses: AssetClass[]
    }

    interface MenuData {
      categories: MenuCategory[]
    }

    const props = defineProps<{
      path: string
    }>()

    const emit = defineEmits<{
      (e: 'toggle'): void
    }>()

    const router = useRouter()
    const { selectedYear } = useYear()

    // Store full index
    // const fullIndex = ref<Record<string, MenuData>>({})
    // We now fetch menu data specifically for the selected year
    const menuData = ref<MenuData>({ categories: [] })

    // Compute current menu data based on selected year
    // const menudata = computed(() => {
    //    return fullIndex.value[selectedYear.value] || { categories: [] }
    // })

    function toggle() {
      emit('toggle')
    }

    function navigateToTransactionCosts() {
      toggle()
      router.push({ name: 'transaction-costs', params: { year: selectedYear.value } })
    }

    function navigateToHowToUse() {
      toggle()
      router.push({ name: 'contents', params: { year: selectedYear.value } })
    }

    function navigateToRespondents() {
      toggle()
      router.push({ name: 'respondents', params: { year: selectedYear.value } })
    }

    // Contact page is generic, no year param needed
    function navigateToContact() {
      toggle()
      router.push({ name: 'contact' })
    }

    function handleAccordionClick(event: Event, sectionId: string, navigationCallback?: () => void) {
      const input = document.getElementById(sectionId) as HTMLInputElement
      const section = input?.closest('.section')

      // If this is a no-submenu section, just call the navigation callback
      if (section?.classList.contains('no-submenu')) {
        if (navigationCallback) {
          navigationCallback()
        }
        return
      }

      // For accordion sections with submenus, handle toggle behavior
      if (input && input.checked) {
        // If already checked, prevent the default behavior and uncheck it
        event.preventDefault()
        input.checked = false
      }
      // If not checked, the label's default behavior will check it
    }

    async function fetchMenuData(year: string) {
      if (!year) return

      const path = `/data/data_${year}/all_charts.json`

      try {
        const response = await axios.get<ChartItem[]>(path, {
          headers: {
              'Content-Type': 'application/json'
          }
        })

        // Group linear list by category
        const flatList = response.data
        const categoriesMap = new Map<string, AssetClass[]>()

        // Use a Set to maintain category order if possible, or just rely on data order
        // const categoriesOrder: string[] = []

        flatList.forEach((item) => {
             // Skip excluded items
             if (item.exclude) return;

             const catName = item.category || 'Other'
             if (!categoriesMap.has(catName)) {
                 categoriesMap.set(catName, [])
                 // categoriesOrder.push(catName)
             }
             categoriesMap.get(catName)?.push({
                 id: String(item.id),
                 name: item.name,
                 pathname: item.pathname
             })
        })

        // Sort categories A-Z
        const sortedCategoriesKeys = Array.from(categoriesMap.keys()).sort((a, b) => a.localeCompare(b))

        const categories: MenuCategory[] = sortedCategoriesKeys.map(cat => ({
            category: cat,
            // Sort items within category A-Z
            assetclasses: (categoriesMap.get(cat) || []).sort((a, b) => a.name.localeCompare(b.name))
        }))

        menuData.value = { categories }

        await nextTick()
        toggleAccordion()
      } catch (e) {
        console.error('Error fetching menu data:', e);
        // Fallback or clear menu
        menuData.value = { categories: [] }
      }
    }

    function toggleAccordion() {
      resetAccordion()
      const accordionItems = document.getElementById('accordion-items')
      if (!accordionItems) return
      const accordionItemLinks = accordionItems.querySelectorAll('a')
      let activeLink: HTMLAnchorElement | undefined

      // Update ARIA expanded states
      const sections = accordionItems.querySelectorAll('.section')
      sections.forEach(section => {
          const input = section.querySelector('input[type="radio"]') as HTMLInputElement
          const label = section.querySelector('label')
          if (input && label) {
             label.setAttribute('aria-expanded', input.checked.toString())
          }
      })

      // 1. Try to find a matching router-link <a>
      accordionItemLinks.forEach((item) => {
        if ((item as HTMLAnchorElement).pathname === props.path) {
          activeLink = item as HTMLAnchorElement
        }
      })

      if (activeLink) {
        // Robustly find the section input
        const section = activeLink.closest('.section')
        if (section) {
            const input = section.querySelector('input[type="radio"]') as HTMLInputElement
            if (input) {
                input.checked = true
                const label = section.querySelector('label')
                if (label) label.setAttribute('aria-expanded', 'true')
            }
        }
        return
      }

      // 2. If no link found, try manual mapping
      const pathToId: Record<string, string> = {
          '/transaction-costs': 'transaction-costs',
          '/contents': 'how-to-use',
          '/respondents': 'respondents',
          '/contact': 'contact'
      }

      const id = pathToId[props.path]
      if (id) {
          const input = document.getElementById(id) as HTMLInputElement
          if (input) {
              input.checked = true
              const label = (input.nextElementSibling as HTMLLabelElement)
              if (label) label.setAttribute('aria-expanded', 'true')
          }
      }
    }

    function resetAccordion() {
      const accordionItems = document.getElementById('accordion-items')
      if (!accordionItems) return
      const inputs = accordionItems.querySelectorAll('input[type="radio"]')
      inputs.forEach(input => {
          (input as HTMLInputElement).checked = false;
          // Reset aria-expanded
          const label = (input.nextElementSibling as HTMLLabelElement)
          if (label) label.setAttribute('aria-expanded', 'false')
      })
    }

    onMounted(() => fetchMenuData(selectedYear.value))

    // Watch path changes
    watch(() => props.path, () => {
      nextTick(() => toggleAccordion())
    })

    // Watch year changes to ensure accordion state is preserved or reset if needed
    // Watch year changes to ensure accordion state is preserved or reset if needed
    watch(selectedYear, (newYear) => {
        // Fetch new menu structure for the year
        fetchMenuData(newYear).then(() => {
             resetAccordion()
             // Always try to toggle accordion to match current route,
             // ensuring cross-year navigation works correctly
             toggleAccordion()
        })
    })
</script>

<style lang="scss" scoped>
    nav {
        flex: 0 0 317px;
        width: 317px;
        background: var(--nav-background);
    }
    /* Let's get this party started */
    .content::-webkit-scrollbar {
        width: 12px;
    }
    /* Track */
    .content::-webkit-scrollbar-track {
        box-shadow: inset 0 0 16px rgba(100, 100, 100, 0.3);
        background: rgb(210, 210, 215);
    }
    /* Handle */
    .content::-webkit-scrollbar-thumb {
        border-radius: 16px;
        background: rgb(123, 159, 165);
        box-shadow: inset 0 0 20px rgba(100, 100, 100, 0.4);
    }
    .content::-webkit-scrollbar-thumb:window-inactive {
        background: rgba(123, 159, 165, 0.4);
    }
    .no-border {
        padding-left: 0 !important;
    }
    .left-menu {
        width: 100%;
        overflow: auto;
        scrollbar-width: thin;
        scrollbar-color: rebeccapurple var(--background-colour);
        background: var(--header-font-colour);
        a {
            color: var(--lcp-black);
        }
    }
    .accordion {
        color: var(--header-font-colour);
        width: 100%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        background: var(--nav-background);
        .section {
            width: 100%;
            input[type="radio"] {
                display: none;
                &:checked {
                    & + label {
                        background: var(--toggle-background-active);
                        color: var(--white);
                        &:before {
                            transform: translateY(-50%) rotate(90deg); /* Rotate arrow down when expanded */
                        }
                    }
                    & ~ .content-wrapper {
                        grid-template-rows: 1fr;
                        /* Ensure visible */
                        .content {
                            opacity: 1;
                            z-index: 10;
                            /* allow internal nav flow */
                        }
                    }
                }
            }
            label {
                position: relative;
                cursor: pointer;
                display: table;
                background: var(--nav-background);
                width: 100%;
                transition: background 0.3s ease-in-out, color 0.3s ease;
                user-select: none;
                span {
                    display: inline-block;
                    padding: 10px 45px 10px 10px; /* Add right padding for arrow space */
                    vertical-align: middle;
                    width: 100%;
                    font-weight: bold;
                }
                &:before {
                    content: "\f105"; /* Font Awesome right arrow */
                    font-family: var(--font-awesome-font);
                    font-weight: 900; /* Solid style for Font Awesome 6 Free */
                    position: absolute;
                    right: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    transition: transform 0.3s ease-in-out;
                    font-size: 14px;
                    color: var(--header-font-colour);
                }
                &:hover {
                    color: var(--toggle-background-active, #ccc);
                }
                a,
                a:active {
                    display: inline-block;
                    color: var(--header-font-colour);
                    width: 100%;
                }
            }
            .content-wrapper {
                display: grid;
                grid-template-rows: 0fr;
                transition: grid-template-rows 0.4s ease-out;
            }

            .content {
                /* No max-height here, use wrapper */
                min-height: 0;
                overflow: hidden;
                background: var(--white);
                box-shadow: inset 0 0 8px rgba(100, 100, 100, 0.5);
                border-radius: 0 0 8px 8px;
                /* Scrollbars if content overflows viewport (handled by parent, but nice to have) */
                li {
                    transition: background-color 1s ease, box-shadow 0.3s ease;
                    padding: 0;
                    margin: 0;
                    &:hover {
                        cursor: pointer;
                    }
                    &:has(.router-link-exact-active) {
                        box-shadow: inset 0 0 8px rgba(200, 200, 200, 0.5);
                        background-color: rgba(0, 0, 0, 0.02);
                    }
                }
                a {
                    display: block;
                    box-sizing: border-box;
                    width: 100%;
                    font-weight: bold;
                    transition: color 0.3s ease;
                    line-height: 1.5rem;
                    padding: 10px 15px;

                    &.router-link-exact-active {
                        color: var(--toggle-background);
                    }
                }
                a:hover {
                    color: var(--toggle-background-active, #ccc);
                }


            }

            /* Hide arrow for sections without submenu - fallback for browsers without :has() support */
            &.no-submenu label:before {
                display: none;
            }

        }
    }
    // Keep these smaller ones if you want specific tweaks for very small screens
    @media only screen and (max-width: 320px) {
        nav {
            width: 100%;
        }
        .accordion {
            .section {
                input[type="radio"] {
                    &:checked {
                        & ~ .content {
                            max-height: 120px;
                        }
                    }
                }
            }
        }
    }
    /* Remove specific mobile overrides as Grid handles this universally */
    @media only screen and (max-width: 480px) {
        nav {
            width: 100%;
        }
    }

    /* Hide mobile-only navigation items on larger screens */
    @include desktop-only {
        .mobile-only {
            display: none;
        }
    }
    @include tablet-only {
        .mobile-only {
            display: none;
        }
    }

    @include mobile-all {
        .left-menu {
            overflow-y: scroll; /* has to be scroll, not auto */
            -webkit-overflow-scrolling: touch;
        }
        .accordion .section label span {
            padding: 10px 45px 10px 10px; /* Adjust padding for smaller screens */
        }
        .accordion .section .content li {
            padding: 8px 8px; /* Adjust padding for smaller screens */
        }
    }
</style>
